import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {FaIcon} from '../../../FaIcon';
import {FACT_TYPE_MODIFIER, factTypeLabelKey} from '../../memory/factConstants';
import {sourceDate, sourceDateTime} from '../../memory/factDates';
import {FactMergeModal} from '../../memory/FactMergeModal';
import {duplicatesByKind, useClientFacts} from '../facts/ClientFactsStore';
import {diffStatements} from './statementDiff';

/**
 * «Duplicados»: los pares de hechos que el motor de la biblioteca comparó y NO pudo cerrar solo.
 *
 * Hasta el 2026-09-12 «Posibles duplicados» vivía en el acto Memoria —la pantalla de lo curado— y le
 * pedía al humano todo par sobre 0,85 de coseno: a la vez frases idénticas y hechos que no tenían
 * nada que ver (Pablo, 10/09). Ahora el backend decide por par: los idénticos y los contenidos uno
 * en el otro se fusionan solos, lo que no se parece se calla, y acá llega sólo lo que exige una
 * persona, en tres grupos que se leen distinto:
 *
 * - «Dicen lo mismo»: una paráfrasis. Un click fusiona con el superviviente sugerido; «Elegir o
 *   editar…» abre el modal de siempre para decidir cuál queda y con qué texto.
 * - «Cambió»: el mismo dato con otro valor. Default «Quedarme con el nuevo» (prioridad al último,
 *   Pablo 10/09); «Mantener los dos» si son de períodos distintos.
 * - «Se contradicen»: sin default. Es la decisión que más vale que vea una persona.
 *
 * Es la cuarta sección hermana debajo del mazo, colapsada, con su propio número: NO entra al «N por
 * revisar» ni al avance. Al pie, «N fusionados solos» lista lo que el motor cerró sin preguntar, con
 * un «Deshacer» por fila que devuelve al perdedor exactamente a donde estaba (biblioteca o cola).
 *
 * Los dos statements van como texto plano (children), nunca interpolados en una clave: los escribió
 * la extracción. Sin propuestas, sin fusiones automáticas y sin error, no se dibuja nada.
 */
const GROUP_TITLE_KEYS = {
    SAME: 'ia_booster.duplicates.group_same',
    UPDATED: 'ia_booster.duplicates.group_updated',
    CONFLICT: 'ia_booster.duplicates.group_conflict',
};

export function DuplicatesSection() {
    const {t} = useTranslation();
    const {duplicates, documentsById, isBusy, actions} = useClientFacts();
    // Colapsada por default: es trabajo que espera, pero no es la cola. Se abre cuando se decide.
    const [open, setOpen] = useState(false);
    const [autoOpen, setAutoOpen] = useState(false);
    // La propuesta que está en el modal («Elegir o editar…»).
    const [choosing, setChoosing] = useState(null);
    const [saving, setSaving] = useState(false);
    // Un Deshacer por veredicto, y nunca dos a la vez sobre el mismo (el mismo remedio que el mazo).
    const undoingRef = useRef({});

    const dups = duplicates || {};
    const proposals = dups.proposals || [];
    const autoMerged = dups.autoMerged || [];
    const stats = dups.stats || {};
    const failed = !!dups.failed;
    const loading = !!dups.loading;
    // Mientras el GET está en vuelo se dibuja el encabezado en gris con la pastilla en esqueleto:
    // el resto del acto ya está pintado, y un hueco donde va «Duplicados» dice «no hay ninguno»
    // sin decirlo. También le reserva el alto, así la sección no entra de golpe empujando el acto
    // Memoria hacia abajo (QA-VIS-dup-04).
    if (loading) {
        return (
            <section className="iabr-dups -loading" aria-busy="true">
                <div className="iabr-dups-head -skeleton">
                    <FaIcon iconName="chevron-right"/>
                    <span className="iabr-dups-title">{t('ia_booster.duplicates.title')}</span>
                    <span className="iabr-dups-count -skeleton"/>
                </div>
            </section>
        );
    }
    if (proposals.length === 0 && autoMerged.length === 0 && !failed) return null;

    const busy = (id) => (isBusy ? isBusy(id) : false);
    const pairBusy = (proposal) => busy(proposal.older.id) || busy(proposal.newer.id);
    const docOf = (fact) => (
        fact && fact.source_document_id ? (documentsById || {})[fact.source_document_id] : null);
    const dateOf = (fact) => {
        const doc = docOf(fact);
        return sourceDate(doc) || sourceDate({created_on: fact && fact.created_on});
    };
    // Cuando los dos hechos caen el MISMO día, la fecha no distingue nada y los rótulos «Anterior»
    // / «Más nuevo» piden una precisión que lo visible no respalda — justo donde hay que decidir
    // cuál es más reciente. Ahí, y sólo ahí, se muestra la hora (QA-VIS-dup-09).
    const whenOf = (proposal, fact) => {
        const mine = dateOf(fact);
        const other = dateOf(fact.id === proposal.older.id ? proposal.newer : proposal.older);
        if (!mine || mine !== other) return mine;
        const doc = docOf(fact);
        return sourceDateTime(doc) || sourceDateTime({created_on: fact && fact.created_on}) || mine;
    };

    const resolve = (proposal, action, extra, okKey) => (
        actions.resolveDuplicate(proposal.id, action, extra).then((ok) => {
            if (ok) toast.success(t(okKey));
            else toast.error(t('ia_booster.deck.error'));
            return ok;
        })
    );
    const handleMerge = (proposal) => resolve(
        proposal, 'merge', {survivor_id: proposal.suggested_survivor_id || proposal.newer.id},
        'ia_booster.duplicates.toast_merged');
    const handleModalMerge = (survivorId, statement) => {
        if (!choosing) return;
        setSaving(true);
        resolve(choosing, 'merge', {survivor_id: survivorId, statement}, 'ia_booster.duplicates.toast_merged')
            .then(() => { setSaving(false); setChoosing(null); });
    };
    const handleKeep = (proposal, action) => resolve(proposal, action, {}, 'ia_booster.duplicates.toast_kept');
    const handleKeepBoth = (proposal) => resolve(proposal, 'keep_both', {}, 'ia_booster.duplicates.toast_kept_both');
    const handleUndo = (entry) => {
        if (undoingRef.current[entry.id]) return;
        undoingRef.current[entry.id] = true;
        actions.undoAutoMerge(entry.id).then((ok) => {
            undoingRef.current[entry.id] = false;
            if (ok) toast.success(t('ia_booster.duplicates.toast_undone'));
            else toast.error(t('ia_booster.deck.error'));
        });
    };

    // El modal preselecciona `facts[0]`: va primero el superviviente sugerido.
    const modalFacts = choosing
        ? [choosing.older, choosing.newer].sort((a, b) => (
            (b.id === choosing.suggested_survivor_id) - (a.id === choosing.suggested_survivor_id)))
        : [];

    // Lo que difiere entre los dos enunciados va resaltado. Sin esto, decidir si «dicen lo
    // mismo» es comparar dos parrafos palabra por palabra: el par mas frecuente de produccion
    // difiere en DOS palabras sobre ciento treinta caracteres.
    const segmentsFor = (proposal, fact) => {
        const diff = diffStatements(proposal.older.statement, proposal.newer.statement);
        return fact.id === proposal.older.id ? diff.a : diff.b;
    };

    const renderFact = (proposal, fact, whenKey) => (
        <div className={`iabr-dups-fact ${fact.id === proposal.suggested_survivor_id ? '-suggested' : ''}`}>
            <span className="iabr-dups-when">
                {t(whenKey)}{whenOf(proposal, fact) ? ` · ${whenOf(proposal, fact)}` : ''}
            </span>
            <p className="iabr-dups-statement">
                {segmentsFor(proposal, fact).map((segment, index) => (segment.changed
                    ? <mark key={index} className="iabr-dups-diff">{segment.text}</mark>
                    : <span key={index}>{segment.text}</span>))}
            </p>
        </div>
    );

    const renderActions = (proposal) => {
        const disabled = pairBusy(proposal);
        if (proposal.kind === 'SAME') {
            return (
                <>
                    <button type="button" className="iabr-btn -primary" disabled={disabled}
                            onClick={() => handleMerge(proposal)}>
                        {t('ia_booster.duplicates.merge')}
                    </button>
                    <button type="button" className="iabr-btn -ghost" disabled={disabled}
                            onClick={() => setChoosing(proposal)}>
                        {t('ia_booster.duplicates.choose')}
                    </button>
                    <button type="button" className="iabr-btn -ghost" disabled={disabled}
                            onClick={() => handleKeepBoth(proposal)}>
                        {t('ia_booster.duplicates.different')}
                    </button>
                </>
            );
        }
        // «Cambió» tiene default (el nuevo); «Se contradicen» no: las tres pesan igual.
        const primary = proposal.kind === 'UPDATED' ? '-primary' : '-ghost';
        return (
            <>
                <button type="button" className={`iabr-btn ${primary}`} disabled={disabled}
                        onClick={() => handleKeep(proposal, 'keep_newer')}>
                    {t('ia_booster.duplicates.keep_newer')}
                </button>
                <button type="button" className="iabr-btn -ghost" disabled={disabled}
                        onClick={() => handleKeep(proposal, 'keep_older')}>
                    {t('ia_booster.duplicates.keep_older')}
                </button>
                <button type="button" className="iabr-btn -ghost" disabled={disabled}
                        onClick={() => handleKeepBoth(proposal)}>
                    {t('ia_booster.duplicates.keep_both')}
                </button>
            </>
        );
    };

    return (
        <section className="iabr-dups" aria-labelledby="iabr-dups-title">
            <button type="button" className="iabr-dups-head" aria-expanded={open}
                    aria-controls="iabr-dups-panel" onClick={() => setOpen(!open)}>
                <FaIcon iconName={open ? 'chevron-down' : 'chevron-right'}/>
                <span className="iabr-dups-title" id="iabr-dups-title">
                    {t('ia_booster.duplicates.title')}
                </span>
                {!(failed && proposals.length === 0) && (
                    <span className="iabr-dups-count">
                        {t('ia_booster.duplicates.count', {count: proposals.length})}
                    </span>
                )}
            </button>
            <p className="iabr-dups-lede">{t('ia_booster.duplicates.lede')}</p>
            {failed && (
                <p className="iabr-dups-failed" role="alert">{t('ia_booster.duplicates.load_failed')}</p>
            )}
            {/* El motor todavía no comparó a todos: la lista puede crecer sola. Se dice para que un
                «0 pares» recién subido el material no se lea como «no hay duplicados». */}
            {stats.pending_scan > 0 && (
                <p className="iabr-dups-scanning">
                    {t('ia_booster.duplicates.scanning', {count: stats.pending_scan})}
                </p>
            )}

            {open && <div id="iabr-dups-panel">
                {duplicatesByKind(proposals).map((group) => (
                    <div key={group.kind} className="iabr-dups-group">
                        <h5 className="iabr-dups-group-title">
                            <span>{t(GROUP_TITLE_KEYS[group.kind])}</span>
                            <span className="iabr-dups-group-count">{group.items.length}</span>
                        </h5>
                        <ul className="iabr-dups-list">
                            {group.items.map((proposal) => {
                                const accent = FACT_TYPE_MODIFIER[proposal.fact_type] || '-other';
                                return (
                                    <li key={proposal.id} className="iabr-dups-row" data-kind={proposal.kind}>
                                        <div className="iabr-dups-chips">
                                            <span className={`iabr-chip -type ${accent}`}>
                                                {t(factTypeLabelKey(proposal.fact_type))}
                                            </span>
                                        </div>
                                        <div className="iabr-dups-pair">
                                            {renderFact(proposal, proposal.older, 'ia_booster.duplicates.older')}
                                            {renderFact(proposal, proposal.newer, 'ia_booster.duplicates.newer')}
                                        </div>
                                        {proposal.reason && (
                                            <p className="iabr-dups-reason">{proposal.reason}</p>
                                        )}
                                        <div className="iabr-dups-acts">{renderActions(proposal)}</div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}

                {autoMerged.length > 0 && (
                    <div className="iabr-dups-auto">
                        <button type="button" className="iabr-dups-auto-toggle" aria-expanded={autoOpen}
                                aria-controls="iabr-dups-auto-list" onClick={() => setAutoOpen(!autoOpen)}>
                            <FaIcon iconName={autoOpen ? 'chevron-down' : 'chevron-right'}/>
                            <span>{t('ia_booster.duplicates.auto_title', {count: autoMerged.length})}</span>
                        </button>
                        {autoOpen && (
                            <>
                                <p className="iabr-dups-lede">{t('ia_booster.duplicates.auto_lede')}</p>
                                <ul className="iabr-dups-auto-list" id="iabr-dups-auto-list">
                                    {autoMerged.map((entry) => (
                                        <li key={entry.id} className="iabr-dups-auto-row"
                                            data-busy={busy(entry.merged && entry.merged.id) || undefined}>
                                            <span className="iabr-dups-auto-merged">
                                                {entry.merged ? entry.merged.statement : ''}
                                            </span>
                                            <span className="iabr-dups-auto-into">
                                                {t('ia_booster.duplicates.auto_into')}
                                            </span>
                                            <span className="iabr-dups-auto-survivor">
                                                {entry.survivor ? entry.survivor.statement : ''}
                                            </span>
                                            <button type="button" className="iabr-btn -ghost"
                                                    disabled={busy(entry.merged && entry.merged.id)}
                                                    onClick={() => handleUndo(entry)}>
                                                {t('ia_booster.deck.undo')}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                                {/* El endpoint deshace CUALQUIER fusión automática, pero la lista
                                    está capada: sin esto hay fusiones reversibles sin superficie
                                    que las ofrezca y la pantalla no lo dice (C-dedup-07). */}
                                {stats.auto_merged_total > autoMerged.length && (
                                    <p className="iabr-dups-auto-more">
                                        {t('ia_booster.duplicates.auto_window',
                                           {listed: autoMerged.length, total: stats.auto_merged_total})}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>}

            <FactMergeModal
                isOpen={!!choosing}
                facts={modalFacts}
                saving={saving}
                toggle={() => setChoosing(null)}
                onMerge={handleModalMerge}
                modalClassName="iabr-dups-modal"
            />
        </section>
    );
}

export default DuplicatesSection;
